import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import QrButton from "../../../Shared/QrButton";
import FilterProjectRow from "../../../Shared/Tables/FilterProjectRow";
import { Search, ChevronLast, ChevronFirst, ChevronRight, ChevronLeft, ArrowDownAZ, Pencil, EllipsisVertical, ChartNoAxesColumn, ReceiptEuro } from "lucide-react";
import { TableContainer, IconButton, Table, TableFooter, TablePagination, TableRow, Paper, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

function TablePaginationActions(props) {
  const theme = useTheme();
  const classes = useStyles();

  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.footer}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <ChevronLast /> : <ChevronFirst />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {theme.direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        {theme.direction === "rtl" ? <ChevronFirst /> : <ChevronLast />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function ProjectTable(props) {
  const navigate = useNavigate();
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const currentDate = new Date();
  const [dropdownItem, setDropdownItem] = useState(null);
  // Avoid a layout jump when reaching the last page with empty rows.
  // const emptyRows =
  //   page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.rows.length) : 0;

  const dropdownRef = useRef(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function reformatDate(dateStr) {
    let dArr = dateStr.split("-");
    return dArr[2] + "/" + dArr[1] + "/" + dArr[0];
  }
  const datesAreOnSameDay = (first, second) => first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();

  function dateRange(a_start, a_end, b_start, b_end) {
    if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
    if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
    if (b_start < a_start && a_end < b_end) return true; // a in b

    if (datesAreOnSameDay(a_start, b_start)) return true;
    if (datesAreOnSameDay(b_end, a_end)) return true;
    return false;
  }

  const getMeasurementCount = (project) => {
    let measurementCount = project.measurementCount;

    if (project.measurementCount === 0) {
      return "No events recorded";
    } else {
      return measurementCount;
    }
  };

  const handleToggleDropdown = (id) => {
    if (dropdownItem) {
      setDropdownItem(null);
      return;
    }
    setDropdownItem(id);
  };

  const handleClickOutsideDropdown = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setTimeout(() => {
        setDropdownItem(null);
      }, 100);
    }
  };

  useEffect(() => {
    if (dropdownItem) {
      document.addEventListener("mousedown", handleClickOutsideDropdown);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, [dropdownItem]);

  const getProjectDateIndicator = (endDate) => {
    const currentDate = new Date();
    const projectEndDate = new Date(endDate);

    if (projectEndDate < currentDate) {
      return <div style={{ backgroundColor: "#D84949", height: "8px", width: "8px", borderRadius: "4px" }}></div>;
    } else if (projectEndDate >= currentDate) {
      return <div style={{ backgroundColor: "#5CC958", height: "8px", width: "8px", borderRadius: "4px" }}></div>;
    }
  };

  return (
    <TableContainer style={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px", overflow: "visible", overflowX: "visible !important" }} component={Paper}>
      <Table size="sm" key={"table"}>
        <thead className={classes.headStyle} key={"thead"}>
          <tr className={classes.tableHeadRow} key={"trow"}>
            <th className={classes.tableHead} style={{ width: "20%" }}>
              <span className={classes.titleSearch}>
                <div className={classes.icons}>
                  <Tooltip title="Alphabetical order" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting("projectName")}>
                      <ArrowDownAZ />
                    </IconButton>
                  </Tooltip>
                  Project
                  <Tooltip title="Search project" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.toggleFilter()}>
                      <Search />
                    </IconButton>
                  </Tooltip>
                </div>
              </span>
            </th>
            <th className={classes.tableHead} style={{ width: "20%" }}>
              <span className={classes.titleSearch}>
                <div className={classes.icons}>
                  <Tooltip title="Alphabetical order" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting("resourceDescription")}>
                      <ArrowDownAZ />
                    </IconButton>
                  </Tooltip>
                  Registration point
                  <Tooltip title="Search registration point" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.toggleFilter()}>
                      <Search />
                    </IconButton>
                  </Tooltip>
                </div>
              </span>
            </th>
            <th className={classes.tableHead} style={{ width: "20%" }}>
              <span className={classes.titleSearch}>
                <div className={classes.icons}>
                  <Tooltip title="Alphabetical order" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting("companyName")}>
                      <ArrowDownAZ />
                    </IconButton>
                  </Tooltip>
                  Company
                  <Tooltip title="Search project" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.toggleFilter()}>
                      <Search />
                    </IconButton>
                  </Tooltip>
                </div>
              </span>
            </th>
            <th className={classes.tableHead} style={{ width: "20%" }}>
              <span className={classes.titleSearch}>
                <div className={classes.icons}>
                  <Tooltip title="Alphabetical order" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting("eventCount")}>
                      <ArrowDownAZ />
                    </IconButton>
                  </Tooltip>
                  Events
                </div>
              </span>
            </th>
            <th className={classes.tableHead} style={{ width: "20%" }}>
              <span className={classes.titleSearch}>
                <div className={classes.icons}>
                  <Tooltip title="Sort descending" placement="top" arrow>
                    <IconButton style={{ borderRadius: 0 }} onClick={() => props.handleSorting("timeRangeBegin")}>
                      <ArrowDownAZ />
                    </IconButton>
                  </Tooltip>
                  Date range
                </div>
              </span>
            </th>
            <th align="center" className={classes.tableHead} style={{ width: "3%", textAlign: "center" }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody key={"tbody"}>
          {props.filterOn && <FilterProjectRow rows={props.rows} filters={props.filters} filterProjects={props.filterProjects} updateFilters={props.updateFilters}></FilterProjectRow>}
          {(props.rows !== undefined && props.rows.length) > 0 ? (
            <React.Fragment>
              {(rowsPerPage > 0 ? props.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : props.rows).map((row) => (
                <tr className={classes.tableBodyRow} key={row.projectId}>
                  <td className={classes.tableData}>{row.projectName}</td>
                  <td className={classes.tableData}>{row.resourceDescription}</td>
                  <td className={classes.tableData}>{row.companyName}</td>
                  <td className={classes.tableData}>{getMeasurementCount(row)}</td>
                  <td className={classes.tableData} style={{ paddingRight: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <p style={{ width: "fit-content" }}>{reformatDate(row.timeRangeBegin.slice(0, 10)) + " - " + reformatDate(row.timeRangeEnd.slice(0, 10))}</p>
                      {getProjectDateIndicator(row.timeRangeEnd)}
                    </div>
                  </td>
                  <td className={classes.actionButtons}>
                    <QrButton
                      disabled={!dateRange(new Date(row.timeRangeBegin), new Date(row.timeRangeEnd), new Date(currentDate), new Date(currentDate))}
                      projectName={row.projectName}
                      resourceName={row.resourceName}
                      QrCode={row.qrCode}
                      size="large"
                    />
                    <IconButton style={{ borderRadius: 0, borderLeft: "1px solid rgba(0, 0, 0, 0.12)" }} onClick={() => navigate(`/modifyproject/${row.qrCode}`)} size="large">
                      <Pencil fontSize="inherit" />
                    </IconButton>
                    <button onClick={() => handleToggleDropdown(row.id)} className={classes.openDropdownButton} style={{ backgroundColor: dropdownItem === row.id ? "#EDEDED" : "transparent" }}>
                      <EllipsisVertical size={20} absoluteStrokeWidth strokeWidth={1.4} />
                    </button>
                    {dropdownItem === row.id ? (
                      <div ref={dropdownRef} className={classes.row__dropdown}>
                        <button className={classes.row__dropdownButton} onClick={() => navigate(`/results/${row.qrCode}`)}>
                          <ChartNoAxesColumn className={classes.iconButton__icon} />
                          Event timeline
                        </button>
                        {/* <button className={classes.row__dropdownButton} onClick={() => navigate(`/projectreport/${row.qrCode}`)}>
                                                <ReceiptEuro className={classes.iconButton__icon} />
                                                Financial dashboard
                                            </button> */}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ) : (
            <tr className={classes.tableBodyRow}>
              <td>No projects available</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
              <td>-</td>
            </tr>
          )}
        </tbody>
        <TableFooter className={classes.tableFooter}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[100, 200, 500, { label: "All", value: -1 }]}
              colSpan={12}
              count={props.rows !== null && props.rows !== undefined ? props.rows.length : 0}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  "aria-label": "rows per page",
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
const useStyles = makeStyles((theme) => ({
  tableHead: {
    height: "42px",
    borderRight: "2px solid #e6ecf0",
  },
  titleSearch: {
    position: "relative",
    display: "flex",
    justifyContent: "flex-start",
    paddingLeft: "16px",
    alignItems: "center",
  },
  headStyle: {
    backgroundColor: "#e0dce4",
  },
  tableBodyRow: {
    justifyContent: "center",
    textAlign: "center",

    position: "relative",
  },
  tableData: {
    textAlign: "left",
    paddingLeft: "24px",
  },
  actionButtons: {
    padding: "0px !important",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  openDropdownButton: {
    width: "48px",
    height: "48px",
    borderRadius: "0px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px",
    margin: "0px",
    color: "#0C0E29",
    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",

    transition: "background-color 0.1s",

    "&:hover": {
      backgroundColor: "#EDEDED !important",
    },
  },

  row__dropdown: {
    position: "absolute",
    right: "0px",
    top: "48px",

    width: "fit-content",
    height: "fit-content",

    padding: "4px",

    backgroundColor: "white",
    zIndex: "999",
    boxShadow: "0px 3px 15px rgba(175, 175, 175, 0.25)",
    borderRadius: "6px",
  },

  row__dropdownButton: {
    width: "100%",
    height: "32px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "medium",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",

    padding: "8px 12px",
    margin: "0px",

    color: "#0C0E29",

    transition: "background-color 0.1s",

    "&:hover": {
      backgroundColor: "#F8F8F8",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    strokeWidth: "1.6px",

    display: "block",
  },

  tableFooter: {
    width: "100%",
  },
  footer: {
    display: "flex",
  },
  icons: {
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));
